import History from '@history';
import { createAsyncThunk, EntityId } from '@reduxjs/toolkit';
import { routesUrl } from 'app/fuse-configs/navigationConfig';
import { RootState } from 'app/store';
import { showMessage } from 'app/store/fuse/messageSlice';
import i18n from 'i18n';
import { createFormApi, deleteFormApi, fetchFormApi, fetchFormsApi, updateFormApi } from '../api';
import { convertFormToPayload, FormUpdateDTO } from '../types';
import { currentFormSelector } from './forms.selector';

const tr = (key: string) => i18n.t(`forms.${key}`, { ns: 'forms' });

const SAVE_ERROR_KEYS: Record<string, string> = {
  STORE_NOT_FOUND: 'builder.errors.storeNotFound',
  INVALID_FORM_ELEMENT: 'builder.errors.invalidElement',
  INVALID_REVIEW_LINK: 'builder.errors.invalidReviewLink',
  INVALID_CUSTOM_LINK: 'builder.errors.invalidCustomLink',
  INVALID_END_GAME: 'builder.errors.invalidEndGame',
  REFERRAL_SMS_MISSING_LINK: 'builder.errors.referralSmsMissingLink',
  REFERRAL_SMS_TOO_LONG: 'builder.errors.referralSmsTooLong',
};

type ApiErrorBody = { error?: { status?: string; details?: { index?: number }[] } };

const saveErrorMessage = (error: unknown, fallbackKey: string) => {
  const body = (error as { response?: { data?: ApiErrorBody } })?.response?.data?.error;
  const elementIndex = body?.details?.[0]?.index;
  if (body?.status === 'INVALID_FORM_ELEMENT' && typeof elementIndex === 'number') {
    return i18n.t('forms.builder.errors.invalidElementAt', { ns: 'forms', number: elementIndex + 1 });
  }
  const status = body?.status;
  return tr(status && SAVE_ERROR_KEYS[status] ? SAVE_ERROR_KEYS[status] : fallbackKey);
};

export const fetchFormsThunk = createAsyncThunk(
  'forms/fetchFormsThunk',
  async ({ page, size }: { page?: number; size?: number }, { dispatch, rejectWithValue }) => {
    try {
      return await fetchFormsApi(page, size);
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('list.errorMessage'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const createFormThunk = createAsyncThunk(
  'forms/createFormThunk',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const { form } = currentFormSelector(getState() as RootState);
      const payload = convertFormToPayload(form);
      const created = await createFormApi(payload);
      History.push(routesUrl.formsHistory);
      dispatch(showMessage({ message: tr('builder.createSuccess'), variant: 'success' }));
      return created;
    } catch (error: unknown) {
      dispatch(showMessage({ message: saveErrorMessage(error, 'builder.createError'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const getFormThunk = createAsyncThunk(
  'forms/getFormThunk',
  async (formId: EntityId, { dispatch, rejectWithValue }) => {
    try {
      return await fetchFormApi(formId);
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('builder.loadError'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const updateFormThunk = createAsyncThunk(
  'forms/updateFormThunk',
  async ({ formId, payload }: { formId: EntityId; payload: FormUpdateDTO }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await updateFormApi(formId, payload);
      dispatch(showMessage({ message: tr('builder.updateSuccess'), variant: 'success' }));
      return updated;
    } catch (error: unknown) {
      dispatch(showMessage({ message: saveErrorMessage(error, 'builder.updateError'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const deleteFormThunk = createAsyncThunk(
  'forms/deleteFormThunk',
  async (formId: EntityId, { dispatch, rejectWithValue }) => {
    try {
      await deleteFormApi(formId);
      dispatch(showMessage({ message: tr('list.deleteSuccess'), variant: 'success' }));
      return formId;
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('list.deleteError'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);
